// @ts-nocheck - TODO: Fix type errors once models have proper types from API client in Phase 4
import dayjs from "dayjs";
import "dayjs/locale/sv";
import type { TagProps } from "@components/Tag";
import type { ConsultantAccount } from "@models/ConsultantAccount";
import type { Job, JobListItem, Shift } from "@models/Job";
import type { Organisation } from "@models/Organisation";
import type { User } from "@models/User";
import type { OoJobService } from "../networking/generated/api-client";
import { appTheme } from "@styles/Theme";
import type { CompetenceItem, JobApplication } from "@models/Schedule";
import type { ShiftItem } from "@models/Order";
import { isValidTime } from "./dateUtils";

dayjs.locale("sv");

export function sleep(ms: number) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

export function isPromise(candidate: any) {
	return !!candidate?.then;
}

export function jobStatus(status: Job["status"]): string {
	switch (status) {
		case "BOOKED":
			return "Bokat";
		case "BOOKED_WITH_UNMANNED_SHIFTS":
			return "Delvis bokat";
		case "CANCELLED":
			return "Avbrutet";
		case "DECLINED":
			return "Nekat";
		case "OPEN":
			return "Ej bokat";
		default:
			return status as string;
	}
}

export function getJobStatusTagProps(job: JobListItem | Job, size = "s"): TagProps | undefined {
	// Completed is not a job status per se, it happens
	// when job is BOOKED and end date has passed plus some padding
	if (job.status === "BOOKED" && dayjs(job.endDate).add(8, "hour").isBefore(dayjs())) {
		return {
			text: "Avslutat",
			background: "white",
			textColor: appTheme.colors.dark,
			// icon: "white-check" as TagProps["icon"],
			size: size as TagProps["size"],
		};
	}

	switch (job.status) {
		case "BOOKED":
			return {
				text: jobStatus(job.status),
				background: appTheme.colors.success,
				icon: "white-check" as TagProps["icon"],
				size: size as TagProps["size"],
			};
		case "BOOKED_WITH_UNMANNED_SHIFTS":
			return {
				text: jobStatus(job.status),
				background: appTheme.colors.lightGreen,
				icon: "green-check" as TagProps["icon"],
				size: size as TagProps["size"],
				textColor: appTheme.colors.darkGreen,
			};
		case "OPEN":
			return {
				text: jobStatus(job.status),
				background: appTheme.colors.dividerLight,
				size: size as TagProps["size"],
				textColor: "#888",
			};
		case "DECLINED":
			return {
				text: jobStatus(job.status),
				background: appTheme.colors.destructive,
				size: size as TagProps["size"],
			};
		case "CANCELLED":
			return {
				text: jobStatus(job.status),
				background: appTheme.colors.lightRed,
				size: size as TagProps["size"],
				textColor: appTheme.colors.darkRed,
			};
	}
}

export function getJobStatusTagDetails(job?: JobListItem | Job): {
	text: string;
	color: string;
	backgroundColor: string;
	iconType?: string;
} {
	if (!job) {
		return {
			text: "",
			backgroundColor: "",
			color: "",
			iconType: "",
		};
	}
	// Completed is not a job status per se, it happens
	// when job is BOOKED and end date has passed plus some padding
	if (job.status === "BOOKED" && dayjs(job.endDate).add(8, "hour").isBefore(dayjs())) {
		return {
			text: "Avslutat",
			backgroundColor: "white",
			color: appTheme.colors.dark,
			iconType: "LOCK",
		};
	}

	switch (job.status) {
		case "BOOKED":
			return {
				text: jobStatus(job.status),
				backgroundColor: appTheme.colors.success,
				color: appTheme.colors.textLight,
			};
		case "BOOKED_WITH_UNMANNED_SHIFTS":
			return {
				text: jobStatus(job.status),
				backgroundColor: appTheme.colors.peach,
				color: appTheme.colors.textDark,
			};
		case "DECLINED":
			return {
				text: jobStatus(job.status),
				backgroundColor: appTheme.colors.destructive,
				color: appTheme.colors.textLight,
			};
		case "CANCELLED":
			return {
				text: jobStatus(job.status),
				backgroundColor: appTheme.colors.lightRed,
				color: appTheme.colors.textDark,
			};
		// case "OPEN":
		default:
			return {
				text: jobStatus(job.status),
				backgroundColor: appTheme.colors.peach,
				color: appTheme.colors.textDark,
			};
	}
}

export function timeReportStatus(status: OoJobService.GetTimereportEntryIdResponse["status"]) {
	switch (status) {
		case "PENDING":
			return "Ej rapporterad";

		case "ATTESTED_BY_ADMIN":
			return "Ej attesterad";

		case "SILENT_ATTESTED":
			return "Attesterad";

		case "ATTESTED_BY_CUSTOMER":
			return "Attesterad";

		case "DECLINED_BY_CUSTOMER":
			return "Nekad";

		case "RESOLVED_DECLINE_BY_ADMIN":
			return "Attesterad";

		default:
			return status;
	}
}

export function isTimeReportAttestet(status: OoJobService.GetTimereportEntryIdResponse["status"]) {
	return ["ATTESTED_BY_CUSTOMER", "RESOLVED_DECLINE_BY_ADMIN", "SILENT_ATTESTED"].includes(status);
}

export function isTimeReportDeclined(status: OoJobService.GetTimereportEntryIdResponse["status"]) {
	return ["DECLINED_BY_CUSTOMER"].includes(status);
}

export function isTimeReportManaged(status: OoJobService.GetTimereportEntryIdResponse["status"]) {
	return isTimeReportAttestet(status) || isTimeReportDeclined(status) || status === "PENDING" || status === "WORKED";
}

export function capitalize(str: string) {
	const tail = str.substring(1, str.length);
	return str.charAt(0).toUpperCase() + tail;
}

export function formatShift(start: Date | string, end: Date | string) {
	return capitalize(dayjs(start).format("ddd D/M HH:mm")) + " - " + formatShiftHours(end);
}

export function formatShiftDate(start: Date | string) {
	return capitalize(dayjs(start).format("ddd D/M"));
}

export function formatDateTime(date: Date | string) {
	return dayjs(date).format("YYYY-MM-DD") + " kl " + dayjs(date).format("HH:mm");
}

export function formatStartEnd(start: Date | string, end: Date | string) {
	return (
		capitalize(dayjs(start).format("ddd D/M")) +
		" - " +
		// Note: subtract tiny bit as end provided from API is midnight monday, but we
		// want it to display Sunday
		dayjs(end).subtract(5, "h").format("ddd D/M")
	);
}

export function formatJobReportStartEnd(start: Date | string, end: Date | string) {
	if (dayjs(start).isSame(end, "day")) {
		return dayjs(start).format("YYYY-MM-DD");
	}

	return dayjs(start).format("YYYY-MM-DD") + " - " + dayjs(end).format("YYYY-MM-DD");
}

export function formatShiftHoursRange(start: Date | string, end: Date | string) {
	return formatShiftHours(start) + " - " + formatShiftHours(end);
}

export function formatShiftHours(date: Date | string) {
	return dayjs(date).format("HH:mm");
}

export function isWeekend(date: dayjs.Dayjs | Date) {
	return dayjs(date).day() === 0 || dayjs(date).day() === 6;
}

export function transferTimeToDate(hhMM: string, date: dayjs.Dayjs | Date | string) {
	const [hourPart, minPart] = hhMM.split(":");
	date = dayjs(date);
	return date.hour(Number(hourPart)).minute(Number(minPart)).second(0).millisecond(0);
}

export function convertMinutesToHours(hours: number) {
	return Math.floor(hours / 60) || 0;
}

export function convertHoursToMinutes(minutes: number) {
	return Math.floor(minutes * 60) || 0;
}

export function updateDateOnly(oldDateWithTime: string, newDay?: Date): string {
	const parsedDate = new Date(oldDateWithTime);

	// Set the new date while keeping the time and timezone information intact
	if (newDay) {
		parsedDate.setFullYear(newDay.getFullYear());
		parsedDate.setMonth(newDay.getMonth());
		parsedDate.setDate(newDay.getDate());
	}

	// Format the updated date back to the string format
	const updatedDateString = parsedDate.toISOString();

	return updatedDateString;
}

export function ellipsis(str: string, maxChars: number) {
	if (str.length < maxChars) {
		return { isEllipsed: false, str, origStr: str };
	}

	return {
		isEllipsed: true,
		str: str.substring(0, maxChars).trim() + "...",
		origStr: str,
	};
}

export function bookingType(type: Job["bookingType"]) {
	switch (type) {
		case "AUTO":
			return "Automatisk bokning";
		case "INVITATION":
			return "Bjud in konsulter";
		case "OPEN":
		default:
			return "En matchande konsult bokas in";
	}
}

export function getHumanReadableCompetences(
	{ branch, service, skills }: Job["competence"],
	orgCompetences?: Organisation["competences"],
	splitCompetences = false
) {
	const selectedBranch = (orgCompetences || []).find((b) => b.key === branch);

	const selectedService = (selectedBranch?.items || []).find((s) => s.key === service);

	if (!skills || skills.length === 0) {
		const theService = selectedService?.name.sv || service;
		return splitCompetences ? [theService] : theService;
	}

	const selectedSkills = skills.map((skill) =>
		selectedService?.items?.find((serviceSkills) => serviceSkills.key === skill)
	);

	if (splitCompetences) {
		return selectedSkills.map((skill) => `${selectedService?.name.sv || service} - ${skill?.name.sv}`);
	}
	return `${selectedService?.name.sv || service} - ${selectedSkills.map((skill) => skill?.name.sv || "").join(", ")}`;
}

export function getConsultantCompetences(
	competences: ConsultantAccount["profile"]["competences"],
	orgCompetences?: Organisation["competences"]
) {
	let competencesArr: string[] = [];

	competences?.map((competence) => {
		const { branch, service, skills } = competence;
		let updatedArrItem: string | string[];

		const selectedBranch = (orgCompetences || []).find((b) => b.key === branch);

		const selectedService = (selectedBranch?.items || []).find((s) => s.key === service);

		if (!skills || skills.length === 0) {
			const theService = selectedService?.name.sv || service;
			updatedArrItem = theService;
		}

		const selectedSkills = skills?.map((skill: any) =>
			selectedService?.items?.find((serviceSkills) => serviceSkills.key === skill)
		);

		if (selectedSkills) {
			updatedArrItem = `${selectedService?.name.sv || service} - ${selectedSkills
				.map((skill: any) => skill?.name.sv || "")
				.join(", ")}`;
			competencesArr.push(updatedArrItem || "");
		} else {
			updatedArrItem = `${selectedService?.name.sv || service}`;
			competencesArr.push(updatedArrItem || "");
		}
	});

	return competencesArr;
}

export function getServiceName({ branch, service }: Job["competence"], orgCompetences?: Organisation["competences"]) {
	if (!orgCompetences) {
		return "";
	}

	const selectedBranch = (orgCompetences || []).find((b) => b.key === branch);

	const selectedService = (selectedBranch?.items || []).find((s) => s.key === service);

	return selectedService?.name.sv || service;
}

export function hasSkills(competenceItem: Organisation["competences"][0]) {
	return competenceItem.items && competenceItem.items.length > 0;
}

export function getStatusDescription(job: Job) {
	// TODO: More descriptions needed?
	if (job.status === "OPEN") {
		return "Uppdraget är publicerat och väntar på att tillsättas.";
	}
	if (job.status === "DECLINED") {
		return "Uppdraget har inte kunnat tillsättas.";
	}
	if (job.status === "CANCELLED") {
		return "Uppdraget har avbrutits och eventuella tidigare bokade konsulter har avbokats.";
	}
	if (job.status === "BOOKED_WITH_UNMANNED_SHIFTS") {
		return "Uppdraget är bokat men har ett eller flera obemmanade pass.";
	}
	return "";
}

export function findLastShift(shifts: Shift[]) {
	return [...shifts].sort((a, b) => b.start.localeCompare(a.start))[0];
}

export function isCustomerUser(user: User) {
	return user.roles?.includes("customer_user");
}

export const isInValidEmail = (email: string): boolean => {
	return !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
};

export const getInitials = (name?: string) => {
	if (!name) {
		return "";
	}
	const names = name.split(" ");
	return names
		.map((n) => n.charAt(0))
		.join("")
		.toUpperCase();
};

export const getCompetenceLabelByKey = (competences: CompetenceItem[]) => {
	const labelByKey = new Map<string, string>();

	competences.forEach((item) => setLabelByKey(labelByKey, item));

	return labelByKey;
};

const setLabelByKey = (labelByKey: Map<string, string>, { key, name, items }: CompetenceItem) => {
	if (name.sv) labelByKey.set(key, name.sv);

	if (items?.length) items.forEach((item) => setLabelByKey(labelByKey, item));
};

export const getShiftsWithStatus = (jobShifts?: ShiftItem[], jobApplications?: JobApplication[]): ShiftItem[] => {
	if (!jobShifts && !jobApplications) {
		return [];
	}

	if (jobShifts) {
		const jobDataList: ShiftItem[] = jobShifts.map((jobShift) => ({
			...jobShift,
			tempStatus: "BOOKED_WITHOUT_CONSULTANT",
		}));

		if (!jobApplications) {
			jobDataList.forEach((jobShift, jobShiftIndex) => {
				if (isValidTime(jobShift.start) && isValidTime(jobShift.end) && jobShift.tempStatus === "DEFAULT") {
					jobDataList.splice(jobShiftIndex, 1, { ...jobShift, tempStatus: "TEMPORARY_ADDED" });
				}
			});
			return jobDataList;
		}

		if (jobApplications) {
			const applicationShiftIds = jobApplications
				.flatMap((jobApplication) => jobApplication.shifts)
				.map((item) => item?.id);

			jobDataList.forEach((jobShift, jobShiftIndex) => {
				if (applicationShiftIds.includes(jobShift.id)) {
					jobDataList.splice(jobShiftIndex, 1, { ...jobShift, tempStatus: "BOOKED_WITH_CONSULTANT" });
				}
				if (isValidTime(jobShift.start) && isValidTime(jobShift.end) && jobShift.tempStatus === "DEFAULT") {
					jobDataList.splice(jobShiftIndex, 1, { ...jobShift, tempStatus: "TEMPORARY_ADDED" });
				}
			});
			return jobDataList;
		}
	}

	return [];
};

export const makeArrayUniqueByURL = (array: { url: string; label: string }[]): { url: string; label: string }[] => {
	const uniqueUrls: Set<string> = new Set();
	const uniqueArray: { url: string; label: string }[] = [];

	array.forEach((item) => {
		if (!uniqueUrls.has(item.url)) {
			uniqueUrls.add(item.url);
			uniqueArray.push(item);
		}
	});

	return uniqueArray;
};

type AnyObject = Record<string, any>;

export const removeUndefinedProperties = (arr: AnyObject[]): AnyObject[] =>
	arr.map((obj) => Object.fromEntries(Object.entries(obj).filter(([_, value]) => value !== undefined)));

export const getErrorInJobCreation = (code: string): string => {
	if (code === "MUST_BE_LOGGED_IN") return "Vänligen logga in igen";
	if (code === "ORDER_NUMBER_ALREADY_EXISTS") return "Ordernummer finns redan";
	if (code === "PRICE_CANNOT_CALCULATE") return "Kan inte beräkna pris för given kompetens";
	if (code === "CONSULTANT_NOT_AVAILABLE")
		return "Konsulten/konsulterna är inte tillgängliga för att skicka inbjudningar";

	// Order creation failed please try again later
	return "Det gick inte att skapa beställning, försök igen senare";
};

export const getErrorInFetchingData = (code: string): string => {
	if (code === "MUST_BE_LOGGED_IN") return "Vänligen logga in igen";

	// Something went wrong please try again
	return "Något gick fel, försök igen";
};
